import React from 'react';
import { observer } from 'mobx-react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { reverse } from 'named-urls';

import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';

import { Button } from 'components/Button';

import routes from 'core/routes';

import AuditScoreChip from 'components/Audits/AuditScoreChip';
import { AuditQuestionDrawerTab } from 'components/Audits/AuditQuestionDrawer';
import { openQuestionParams, QUESTION_PARAM } from 'components/Audits/questionDrawer';

import { AuditScoreQuestion, AuditScoreSection } from 'utils/api/modules/entities/entities.types';

import AuditRecordStore from 'stores/AuditRecord';

import { TabComponentProps } from 'components/Entities/EntityDetailsContent/types';

import AuditQuestionRow from './AuditQuestionRow';
import { CollapseButton, EmptyState, SectionCard, SectionHeader, SectionTitle, TabWrapper } from './styles';

// The tab config types every tab against the whole store union; narrow the audits-only surface here.
const AuditAnswersTab: React.FunctionComponent<TabComponentProps> = ({ store }): React.ReactElement => {
  const auditRecord = store as AuditRecordStore;
  const details = auditRecord.details;

  // The open question lives in the URL: linkable, reload-safe, and the tab config passes only the store.
  const [searchParams, setSearchParams] = useSearchParams();
  const [collapsed, setCollapsed] = React.useState<Record<string, boolean>>({});
  const [continuing, setContinuing] = React.useState(false);
  const navigate = useNavigate();

  // A started-but-never-opened scheduled audit lands here with an empty tab and no way forward.
  const onContinue = async (): Promise<void> => {
    setContinuing(true);
    try {
      const draftId =
        auditRecord.details?.draft?.uuid || (await auditRecord.bindDraft(auditRecord.details?.uuid || ''));
      if (draftId) {
        navigate(reverse(routes.dashboard.audits.draft.details, { draftId }));
      }
    } finally {
      setContinuing(false);
    }
  };

  // One request covers every question, so the row badges are populated on first paint.
  React.useEffect(() => {
    auditRecord.questionActivities?.loadStats();
  }, [auditRecord.questionActivities]);

  const openDrawer = (questionKey: string, tab: AuditQuestionDrawerTab): void =>
    setSearchParams(openQuestionParams(searchParams, questionKey, tab), { replace: true });

  const openQuestionKey = searchParams.get(QUESTION_PARAM);

  if (!details) {
    return <></>;
  }

  // A record carrying only `legacySubmission` is a migrated audit: submitted, nothing to render.
  if (!details.submission && details.legacySubmission) {
    return (
      <EmptyState data-testid="audit-answers-migrated">
        <p>This audit was migrated from the previous Audits app, so its answers cannot be shown here.</p>
      </EmptyState>
    );
  }

  if (!details.submission) {
    return (
      <EmptyState data-testid="audit-answers-empty-in-progress">
        <p>This audit hasn&apos;t been submitted yet.</p>
        <Button variant="contained" onClick={onContinue} disabled={continuing} data-testid="audit-answers-continue">
          Continue audit
        </Button>
      </EmptyState>
    );
  }

  const { form, stack } = details.submission;

  // compute_audit_score walks form.order, not `stack` — index by key, never zip by position.
  const scoreSections = new Map<string, AuditScoreSection>(
    (details.score?.sections || []).map((section) => [section.key, section])
  );
  const scoreQuestions = new Map<string, AuditScoreQuestion>(
    (details.score?.sections || []).flatMap((section) => section.questions.map((question) => [question.key, question]))
  );

  return (
    <TabWrapper data-testid="audit-answers-tab">
      {stack.map((sectionKey) => {
        const section = form.form.properties[sectionKey];

        // A stack entry with no section survives a template edit that removed it.
        if (!section) {
          return null;
        }

        const sectionScore = scoreSections.get(sectionKey);
        const expanded = !collapsed[sectionKey];

        return (
          <SectionCard key={sectionKey}>
            <SectionHeader $expanded={expanded} data-testid={`audit-section-${sectionKey}`}>
              <CollapseButton
                type="button"
                aria-expanded={expanded}
                aria-label={expanded ? 'show less' : 'show more'}
                data-testid={`audit-section-toggle-${sectionKey}`}
                onClick={() => setCollapsed((current) => ({ ...current, [sectionKey]: !current[sectionKey] }))}
              >
                {expanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
              </CollapseButton>
              <SectionTitle>{section.title}</SectionTitle>
              {/* The answered-count is deliberately absent: every section of a submitted audit is
                  complete, so `3/3` beside `26/30 | 87%` is two fractions saying different things. */}
              <AuditScoreChip
                percentage={sectionScore?.percentage}
                variant="filled"
                label={
                  sectionScore && sectionScore.earned !== null && sectionScore.max !== null
                    ? `${sectionScore.earned}/${sectionScore.max}`
                    : undefined
                }
                data-testid={`audit-section-score-${sectionKey}`}
              />
            </SectionHeader>

            {expanded &&
              section.order.map((questionKey) => (
                <AuditQuestionRow
                  key={questionKey}
                  store={auditRecord}
                  questionKey={questionKey}
                  question={section.properties[questionKey]}
                  mandatory={(section.required || []).includes(questionKey)}
                  open={questionKey === openQuestionKey}
                  score={scoreQuestions.get(questionKey)}
                  onOpenDrawer={openDrawer}
                />
              ))}
          </SectionCard>
        );
      })}
    </TabWrapper>
  );
};

export default observer(AuditAnswersTab);
